export const isSafeInternalRedirectPath = (path: string): boolean => {
  return path === '/' || /^\/[^/\\]/.test(path);
};

export const getSafeRedirectPath = (
  redirectPath: string | null
): string | null => {
  if (redirectPath == null) return null;

  const normalizedRedirectPath = redirectPath.trim();
  if (normalizedRedirectPath.length === 0) return null;

  // 相対パスは引き続き許可（オープンリダイレクト防止のため先頭が二重スラッシュやスラッシュ+バックスラッシュの形式は拒否）。
  if (normalizedRedirectPath.startsWith('/')) {
    if (!isSafeInternalRedirectPath(normalizedRedirectPath)) return null;
    return normalizedRedirectPath;
  }

  // 絶対 URL は、https/http のみ許可する。
  let redirectUrl: URL;
  try {
    redirectUrl = new URL(normalizedRedirectPath);
  } catch {
    return null;
  }

  if (!['http:', 'https:'].includes(redirectUrl.protocol)) return null;
  if (redirectUrl.username || redirectUrl.password) return null;

  return redirectUrl.toString();
};

const getOriginFromUrl = (rawUrl: string | null | undefined): string | null => {
  if (rawUrl == null) return null;

  try {
    return new URL(rawUrl).origin;
  } catch {
    return null;
  }
};

const getDefaultSetupProfileRedirectOrigins = (): string[] => {
  return [
    window.location.origin,
    getOriginFromUrl(import.meta.env.VITE_DOLPHIN_URL),
  ].filter((origin): origin is string => origin != null);
};

export const getSafeSetupProfileRedirectPath = (
  redirectPath: string | null,
  allowedOrigins = getDefaultSetupProfileRedirectOrigins()
): string | null => {
  const safeRedirectPath = getSafeRedirectPath(redirectPath);
  if (safeRedirectPath == null) return null;

  if (isSafeInternalRedirectPath(safeRedirectPath)) {
    return safeRedirectPath;
  }

  try {
    const redirectUrl = new URL(safeRedirectPath);
    if (!allowedOrigins.includes(redirectUrl.origin)) return null;

    return redirectUrl.toString();
  } catch {
    return null;
  }
};

export const getSafeExternalUrl = (
  rawUrl: string | null | undefined
): string | null => {
  if (rawUrl == null) return null;

  const urlText = rawUrl.trim();
  if (urlText.length === 0) return null;

  try {
    const parsedUrl = new URL(urlText);
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) return null;
    return parsedUrl.toString();
  } catch {
    return null;
  }
};

export const createUrlWithRedirectQuery = (
  basePath: string,
  redirectPath: string | null,
  origin = window.location.origin
): string => {
  const baseUrl = new URL(basePath, origin);
  const safeRedirectPath = getSafeRedirectPath(redirectPath);

  if (safeRedirectPath) {
    baseUrl.searchParams.set('to', safeRedirectPath);
  }

  return baseUrl.toString();
};
